import classNames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { CloseButton } from "../CloseButton";
import { Overlay } from "../Overlay";
import { Tooltip } from "../Tooltip";
import "./modal.scss";
import { getContentTarget } from "../../utils";

type TModalSize = "sm" | "md" | "lg" | "xl";

export interface TModal extends React.HTMLAttributes<HTMLDialogElement> {
    children: React.ReactNode;
    open: boolean;
    onOpen?: () => void;
    onClose: () => void;
    size?: TModalSize;
    customWidth?: string | number;
    closeBtn?: boolean;
    closeOnOutsideClick?: boolean;
    closeOnEscape?: boolean;
    fromTop?: string;
    bgColor?: "white" | "light-grey";
    className?: string;
    closeBtnTooltipContent?: string | JSX.Element;
    showCloseBtnTooltip?: boolean;
    overflowVisible?: boolean;
    isImageGallery?: boolean;
    id?: string;
}

const getWidth = (x: TModalSize) => {
    return {
        sm: 328,
        md: 488,
        lg: 800,
        xl: 1040,
    }[x];
};

export const Modal: React.FC<TModal> = ({
    children,
    open,
    onOpen,
    onClose,
    size = "md",
    customWidth = getWidth(size),
    closeBtn = true,
    closeOnOutsideClick = true,
    closeOnEscape = true,
    fromTop,
    bgColor = "white",
    className = "",
    closeBtnTooltipContent,
    showCloseBtnTooltip = false,
    overflowVisible = false,
    isImageGallery = false,
    id,
    ...props
}: TModal) => {
    const modalRef = useRef<HTMLDialogElement | null>(null);
    useOutsideClick(closeOnOutsideClick && open, modalRef, onClose);

    const handleEscapeKey = useCallback(() => {
        closeOnEscape && onClose();
    }, [closeOnEscape, onClose]);

    // Focus trap
    const handleTabKey = useCallback((e: KeyboardEvent) => {
        if (!modalRef.current) return;

        const focusableModalElements = modalRef.current.querySelectorAll<HTMLElement>(
            'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
        );

        const firstElement = focusableModalElements[0];
        const lastElement = focusableModalElements[focusableModalElements.length - 1];

        if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement.focus();
            return e.preventDefault();
        }
    }, []);

    useEffect(() => {
        open && modalRef.current && modalRef.current.focus();

        if (open && fromTop && modalRef.current) {
            window.scrollTo(0, 0);
        }

        onOpen && onOpen();
        //eslint-disable-next-line
    }, [open, fromTop]);

    // Add eventlistener for keys
    useEffect(() => {
        const keyListenersMap = new Map([
            ["Escape", handleEscapeKey],
            ["Tab", handleTabKey],
        ]);

        function keyListener(e: KeyboardEvent) {
            const listener = keyListenersMap.get(e.key);
            return listener && listener(e);
        }

        if (open) {
            document.addEventListener("keydown", keyListener);
        } else {
            document.removeEventListener("keydown", keyListener);
        }

        return () => document.removeEventListener("keydown", keyListener);
    }, [open, handleEscapeKey, handleTabKey]);

    return createPortal(
        <>
            <Overlay show={open} opacity={isImageGallery ? "high" : "normal"} />
            {open && (
                <dialog
                    {...props}
                    ref={modalRef}
                    id={id}
                    className={classNames("c-modal", className, {
                        "c-modal--from-top": fromTop,
                        "c-modal--light-grey": bgColor === "light-grey",
                        "c-modal--overflow-visible": overflowVisible,
                        "c-modal--image-gallery": isImageGallery,
                    })}
                    open={open}
                    style={{ width: customWidth, ...(fromTop && { top: fromTop }) }}
                    tabIndex={0}
                >
                    {closeBtn && !showCloseBtnTooltip && (
                        <CloseButton
                            onClick={onClose}
                            className="c-modal__close-btn"
                            onDarkBackground={isImageGallery}
                        />
                    )}
                    {closeBtn && closeBtnTooltipContent && showCloseBtnTooltip && (
                        <div className="c-modal__close-btn">
                            <Tooltip triggerOnClick clickable normalFontSize toolTipContent={closeBtnTooltipContent}>
                                <CloseButton onClick={onClose} onDarkBackground={isImageGallery} />
                            </Tooltip>
                        </div>
                    )}
                    {children}
                </dialog>
            )}
        </>,
        getContentTarget()
    );
};

export default Modal;
